import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import AspectRatio from '@/components/common/AspectRatio';
import Loading from '@/components/switchback/Loading/Loading';
import ResponsiveImage from '@/components/utility/ResponsiveImage/ResponsiveImage';
import { breakpoint } from '@/constants/breakpoint';
import useWindowSize from '@/hooks/useWindowSize';
import { IImage } from '@/services/types/search/rentals/id';
import formatImageURL from '@/utility/formatImageURL';
import { getImageSizeAndProportion } from '@/utility/getImageSizeAndProportion';
import { isSSR } from '@/utility/isSSR';
import { getYoutubeVideoId } from '@/utility/youtubeParser';

import CaptionBox from '../../CaptionBox/CaptionBox';
import css from './GalleryCarousel.module.css';

const ImagePlaceholder: React.FC<{ image: IImage; imageLoaded: boolean; parent: HTMLElement }> = ({
  image,
  imageLoaded = false,
  parent,
}) => {
  const windowSize = useWindowSize();
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const img = !isSSR() && document.createElement('img');
    const wait = setInterval(() => {
      if (img) {
        /* Check if the image has already been added */
        if (img.src === '') {
          img.src = formatImageURL(image.url, 'default', 'fill');
        }
        const w = img.naturalWidth;
        const h = img.naturalHeight;

        const padding = windowSize.width && windowSize.width > breakpoint.xxxl ? 36 : 0;

        if (w && h) {
          clearInterval(wait);
          /* Set proportional image size to the placeholder */
          const maxWidth = parent ? parent.offsetWidth - padding : 50;
          const maxHeight = parent ? parent.offsetHeight - padding : 50;
          const { width: newWidth, height: newHeight } = getImageSizeAndProportion({
            width: w,
            height: h,
            maxWidth,
            maxHeight,
          });
          setWidth(newWidth);
          setHeight(newHeight);
        }
      }
    }, 50);

    imageLoaded && clearInterval(wait);

    return () => clearInterval(wait);
  }, [image, parent, windowSize.width, imageLoaded]);

  return (
    <Loading
      className={`transition-opacity duration-300 ease-in-out flex items-center justify-center pointer-events-none absolute z-10 sm:rounded-md z-20 ${
        width > 50 ? 'bg-gray-100' : ''
      }`}
      style={{
        width,
        height,
        maxWidth: '100%',
        maxHeight: '100%',
        opacity: imageLoaded ? 0 : 1,
      }}
    />
  );
};

const Video: React.FC<{ image: IImage; handleImageLoad: () => void }> = ({
  image,
  handleImageLoad,
}) => {
  return (
    <AspectRatio
      ratio="16:9"
      hasImageHover={false}
      className={`${css.iframeHolder} w-full lg:h-full`}>
      <iframe
        title={image.description}
        width="100%"
        height="100%"
        onLoad={handleImageLoad}
        src={`https://www.youtube.com/embed/${getYoutubeVideoId(
          image.url,
        )}?enablejsapi=1&output=embed`}
        frameBorder={0}
        allow="autoplay; encrypted-media"
        allowFullScreen
      />
    </AspectRatio>
  );
};

const Image: React.FC<{ image: IImage; handleImageLoad: () => void }> = ({
  image,
  handleImageLoad,
}) => {
  const intl = useIntl();
  return (
    <>
      <ResponsiveImage
        className="sm:rounded-md"
        src={image.url}
        alt={image.alt ?? image.ai_description ?? image.description}
        onLoad={handleImageLoad}
      />
      {image.description && (
        <CaptionBox
          className={`absolute bottom-0 left-0 right-0 z-20 shadow-200 lg:left-auto lg:bottom-8 lg:right-8 ${css.captionBox}`}
          linkText={intl.formatMessage({ defaultMessage: 'Show details', id: 's2XIgr' })}
          linkTextCollapsed={intl.formatMessage({ defaultMessage: 'Hide details', id: 'X5Q310' })}
          header={image.tags}>
          {image.description}
        </CaptionBox>
      )}
    </>
  );
};

const CarouselItem: React.FC<{ image: IImage; className: string }> = ({ image, className }) => {
  const parentRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
  const parent = parentRef.current as HTMLDivElement;
  const [imageLoaded, setImageLoaded] = useState(false);

  const handleImageLoad = useCallback(() => {
    setImageLoaded(true);
  }, []);

  return (
    <div key={image.id} className={`${className} relative`}>
      <div
        ref={parentRef}
        className={`${css.galleryItem} relative flex items-center h-full justify-center`}>
        <ImagePlaceholder imageLoaded={imageLoaded} parent={parent} image={image} />
        {image.video ? (
          <Video image={image} handleImageLoad={handleImageLoad} />
        ) : (
          <Image image={image} handleImageLoad={handleImageLoad} />
        )}
      </div>
    </div>
  );
};

export default CarouselItem;
